// Import React and hooks from React
import React, { useEffect, useMemo, useState} from "react";

// Import global hooks
import useFormHandler from "global/hooks/useFormHandler";

// Import hooks
import useCustomProductsData from "../../hooks/useCustomProductsData";
import { useToast } from "global/hooks/useToast";

// Import components
import CreateButton from "global/components/CreateButton";
import {SettingsEditor} from "./MMMSettings";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";

// Import css stylesheet
import "./ManageMMMs.css";



// Exported component in PascalCase as requested
export default function MMMManager({

  // Optional initial type selection: "materials" | "methods" | "modes"
  initialType = "select",

  materials, 
  setMaterials,
  methods, 
  setMethods,
  modes, 
  setModes,
  // Function to get MMMs
  fetchMMMs,
  setLinks,
  setLoadingLinks,
  handleRefetchMMMsAndLinks

}) {

  // Define toast options
  const { toastError } = useToast();

    // Custom hook for fetching and managing pod design and product data
    const {
        fetchCustomProductsMaterials,
        fetchCustomProductsMethods,
        fetchCustomProductsModes,
        updateAddCustomProductsMMM,
        removeCustomProductsMMM,
    } = useCustomProductsData(); 

  // -----------------------------
  // Sidebar state (type + sorting)
  // -----------------------------

  // The currently selected collection type on the left dropdown
  const [selectedType, setSelectedType] = useState(initialType);

  // The current sort mode for the scrollable list ( "newest" | "oldest" | "az" | "za" )
  const [sortMode, setSortMode] = useState("newest"); 

  // The raw list fetched for the current type
  const [rawList, setRawList] = useState([]);

  // Loading state for list operations
  const [isLoadingList, setIsLoadingList] = useState(false);

  // Potential error text for list operations
  const [listError, setListError] = useState("");

  // The id of the currently selected item in the list
  const [selectedId, setSelectedId] = useState(null);

  // Whether we are in "Add New" mode (show blank form)
  const [isAddingNew, setIsAddingNew] = useState(false);

  // Store details for current selected M, M or M
  const [itemDetails, setItemDetails] = useState([]);

  // State for checking whether data is edittable or not (core strich data)
  const [isCoreData, setIsCoreData] = useState(null);


  // -------------------------------------
  // Form state (detail panel on the right)
  // -------------------------------------


  // -------------------------------
  // useFormHandler
  // -------------------------------


   // Set state to check if form is partly edited
    const [formIsPartlyEdited, setFormIsPartlyEdited] = useState(false);

    // Memoize the initial form values to avoid recalculating unless details change
    const initialFormValues = useMemo(() => {
        
        if (itemDetails) {

            // Return the initial form values
            return {

                // Spread all existing connection details into the form state
                ...itemDetails,
                //active: S2B(itemDetails.active)

                // Use the formatted items
              settings: itemDetails.settings || []

            };

        }
    
    // Dependency array to recompute initialFormValues only when details change

    }, [itemDetails]);



    // Extract form-related state and functions from the `useFormHandler` custom hook
    const {

        formValues,     // Stores the current values of form inputs
        handleChange,   // Function to update form values when inputs change
        //handleSubmit,   // Function to handle form submission logic
        resetForm,      // Function to reset data on the form
        isFormPartlyEdited, // Check to see if form has been partially edited
        //fieldErrors,    // Object storing validation errors for individual fields
        //formError,      // General error message (e.g. API errors)
        //loading,        // Boolean indicating whether the form is submitting
        //apiResponse,    // Stores the response from the API after submission

    } = useFormHandler({

        // Default values for the form fields
        initialValues: initialFormValues,

        // Enable syncing - because we're using memo to remeber the initial form values before the user edits them
        syncInitialValues: true,

        // API function to be called on form submission based on adding a new or updating an existing one
        //apiCall: addingNewMMM ? addNewMMM : updateMMM,

        // We want to convert boolean to string
        convertBooleans: true,

        // We want to convert number strings to numbers
        convertNumbers: true,

        // Validation function to check the form fields before submission
        validate: (values) => {

            //console.log("error values: ", values);

            // Set errors to empty
            const errors = {};

            // Validation rules for form fields

            // Don't allow spaces in alias
            /*
            if (values.alias?.includes(' ')) {
                errors.alias = '*Spaces are not allowed in this field.';
            }
            */

            // Return validation errors (if any)
            return errors;

        },

    });



    // Check if the form is partly edited before updating the values
    useEffect(() => {

        // Check if the form is partly edited
        if (setFormIsPartlyEdited) {

            // It is so set state
            setFormIsPartlyEdited(isFormPartlyEdited());
        } 

        // Re-evaluate when form values change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues]);



  // --------------------------------------
  // Fetch list whenever the type selection changes
  // --------------------------------------
 
  const fetchList = async (type) => {
  
    // Clear previous errors when type changes
    setListError("");

    // Reset current selection
    setSelectedId(null);

    // Exit "Add New" mode when type changes
    setIsAddingNew(false);

    // Format "materials" | "methods" | "modes"
    const key = String(type || "").toLowerCase(); 

    // List of apiCalls
    const apiCalls = {

      materials: fetchCustomProductsMaterials,

      methods: fetchCustomProductsMethods,

      modes: fetchCustomProductsModes,

    };

    // Set the correct call based on the type
    const apiCall = apiCalls[key]; 

    // Set loading state
    setIsLoadingList(true);

    try {

      // Make the call
      const list = apiCall ? await apiCall() : [];

      // Push response to state
      setRawList(list);

      // Return the list immediately so it can be used by UI
      return list;

    } catch (err) {

      console.error("FetchList failed:", err);

      // Safe fallback - empty rawList
      setRawList([]);

      // Handle errors if we want
      throw err; 

    } finally {

      // Clear loading state
      setIsLoadingList(false); 

    }
    
  };



  // -------------------------
  // Sorting utility for lists
  // -------------------------
  const sortItems = (items, mode) => {

    // Create a shallow copy before sorting
    const copy = [...items];

    // Decide the sort by mode
    if (mode === "newest") {

      // Sort by id descending (assumes larger id == newer)
      copy.sort((a, b) => (b?.id ?? 0) - (a?.id ?? 0));

    } else if (mode === "oldest") {

      // Sort by id ascending
      copy.sort((a, b) => (a?.id ?? 0) - (b?.id ?? 0));

    } else if (mode === "az") {

      // Sort by name ascending
      copy.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));

    } else if (mode === "za") {

      // Sort by name descending
      copy.sort((a, b) => (b?.name || "").localeCompare(a?.name || ""));

    }

    // Return the sorted copy
    return copy;

  };

  // Memoize the sorted list for rendering
  const sortedList = useMemo(() => sortItems(rawList, sortMode), [rawList, sortMode]);



  // ------------------------------
  // Selection + form initialization
  // ------------------------------


  // When a list item is clicked, populate the right form
  const handleSelectItem = (id, listOverride) => {

    // Exit "Add New" mode if we were in it
    setIsAddingNew(false);

    // Check listOverride is an array if provided
    const checkOverrideList = Array.isArray(listOverride) ? listOverride : null;

    // Set the list to look in as the override list if there is one (we've added a new item) otherwise use the stored rawList 
    const list = checkOverrideList ? checkOverrideList : rawList;

    // Find the item in the list
    const item = list.find((x) => x.id === id);

    // Set selected id
    setSelectedId(id);

    // Initialize form values by setting the itemDetails
    setItemDetails(item || null);

    // Check if it's a strich setting (is the id above 1000000, if so it's a strich product as they all start with 1000000) - if so we don't want to allow editting or deletion
    // NOTE: will change this to check for a flag instead at some point i.e item.strich_flag ?
    if (id > 1000000 ) {

      // It's core data so set state to true
      setIsCoreData(true);

    } else {

      // It's not core data so set state to false
      setIsCoreData(false);

    }

  };



  // --------------------
  // Add new item handler
  // --------------------
  const handleAddNewMMM = () => {

    // Enter "Add New" mode
    setIsAddingNew(true);

    // Clear the current selection
    setSelectedId(null);

    // Clear isCoreData
    setIsCoreData(false);

    // Start a blank template with sane defaults
    const blank = {
      // id is not yet assigned
      name: "",
      description: "",
      dpi: "",
      settings: [],
    };

    // Initialize form values by setting the itemDetails
    setItemDetails(blank);

  };



  // ----------------------------
  // Delete currently selected item
  // ----------------------------

  const handleDelete = async (type, id) => { 

    // Create delete message
    const message = type === "Materials" ? "material" : type === "Methods" ? "method" : type === "Modes" ? "mode" : "";

    // Display a confrimation dialog
    DisplayConfirmationDialog(

        // Dialog message
        "Are you sure you want to delete this " + message + "?",

        // Confirm button title
        "Delete",

        // Cancel button title
        "Cancel",

        // Returned response from the dialog box
        async function (is_confirmed) {
          
          // Return early if they cancel
          if (!is_confirmed) return;

          try { 

            // Make the call
            const response = await removeCustomProductsMMM(type, id)

            // Check if it was successful
            if (response.success) {

              // Set loading state  
              setIsLoadingList(true);

              // Refetch MMMs and Links
              handleRefetchMMMsAndLinks(setMaterials, setMethods, setModes, setLinks, setLoadingLinks);

              // Refetch the list (without the deleted MMM)
              await fetchList(type);

              // Clear the selected id
              setSelectedId(null);
             
            } else {

              // Alert toast error
              toastError({ title: "Failed", message: "Could not delete " + message + ": " + response.error_message, autoCloseMs: 6000 });

            }

          } catch (error) {

            console.error("Error deletingMMM:", error);

          } finally {

            // Clear loading state  
            setIsLoadingList(false);

          }
          
        }
        
    );

  };



  // -------------
  // Save handler
  // -------------


  const handleSave = async (type) => {

    // Create save message
    const message = type === "Materials" ? "material" : type === "Methods" ? "method" : type === "Modes" ? "mode" : "";

    // Clear previous errors when type changes
    setListError("");

    // Mark loading
    setIsLoadingList(true);

    // Set the payload as the formValues
    const payload = formValues ?? {};

    // Delete dpi from the payload (api complains)
    // Update we don't need dpi in payload! - Should remove it from UI
    delete payload.dpi;

    try {

      // Make the call
      const response = await updateAddCustomProductsMMM(type, payload);

      // Check if it was successful
      if (response.success) {


          // Refetch MMMs and Links - this should just fetch the MMMs not links as well because it makes the links table flicker unecessarily
          handleRefetchMMMsAndLinks(setMaterials, setMethods, setModes, setLinks, setLoadingLinks);

          // Refetch the list with the new updated/added MMM
          const list = await fetchList(type);

          // Exit "Add New" mode when done
          setIsAddingNew(false);

          // If we're adding new then get id from response else we're updating so use selectedId
          const id = isAddingNew ? response.results.id : selectedId;

          // Select the item that we've just updated or added
          handleSelectItem(id, list);

          // Clear adding new state
          setIsAddingNew(false);

          // Clear the partially editted state
          setFormIsPartlyEdited(false);

      } else {

        // Alert toast error
        toastError({ title: "Failed", message: "Could not add " + message + ": " + response.error_message, autoCloseMs: 6000 });

      }

    } catch (error) {

      console.error("Error updatingMMM:", error);

    } finally {

      // Clear loading state
      setIsLoadingList(false);

      // Clear adding new state
      setIsAddingNew(false);

    };

  };



  // ---------------
  // Cancel handler
  // ---------------
  const handleCancel = () => {

    // useFormHalder to reset the form
    resetForm();

    // Update flag to force SettingsEditor remount
    setResetSettingsFlag(n => n + 1);

  };



// Flag for resetting/remounting the SettingsEditor 
const [resetSettingsFlag, setResetSettingsFlag] = useState(0);


/* ----------------- New Settings Editor ---------------- */

// Create an empty_settings value to fall back to when rendering settingsEditor (to handle adding a setting when there are none)
const EMPTY_SETTINGS = Object.freeze([]);

// Define a function to render the Settings section; accepts the currently selected item's details
const RenderSettingsSection = (itemDetails) => {

  // Compute a stable key that changes only when we switch record context (selectedId) or add-new mode
  const watchKey = isAddingNew ? "new" : String(selectedId ?? "none");

  // Prefer the editable form value if present; else the item's settings; else a stable empty (no hooks used)
  const currentSettings =
    Array.isArray(formValues?.settings) ? formValues.settings
    : Array.isArray(itemDetails?.settings) ? itemDetails.settings
    : EMPTY_SETTINGS;

  // Define a handler that receives the new settings array from the child and forwards it to the form handler
  const handleSettingsChange = (newSettingsArray) => {

    // Call the generic form change handler with a synthetic event targeting the "settings" field
    handleChange({

      // Provide a minimal event-like target object for the form handler
      target: {

        // Name of the field we are updating in formValues
        name: "settings",

        // New value for that field (array of singleton objects)
        value: newSettingsArray,

      },

    });

  };

  // Return the JSX that renders the Settings editor block
  return (

    // Wrapper block for the Settings area using BEM class names
    <div className="array-manager__group">

      <h3 className="array-manager__title">Settings</h3>
      
      {/* Render the reusable SettingsEditor component */} 
      <SettingsEditor

        // Remount trigger when user clicks 'cancel' by updating the ResetSettingsFlag
        key={`${watchKey}:${resetSettingsFlag}`}  

        // Pass the current settings (array-of-singleton objects) to the editor
        settingsArray={currentSettings}

        // Provide the change callback so the editor can push updates upward
        onSettingsChange={handleSettingsChange}

        // Disable inputs when editing core/locked data
        disabled={isCoreData}

        // Tell the editor when the selected record changes so it can re-sync its local draft
        watchKey={watchKey}

      />

    </div>

  );

};



  // -----------------------
  // Render helpers
  // -----------------------

  // Render any top-level keys except id and settings as text inputs (name, description, dpi, etc.)
  const renderTopLevelFields = () => {

    // Determine the keys present in current form
    const keys = Object.keys(formValues || {});

    // Filter out id and settings
    const topLevel = keys.filter((k) => k !== "id" && k !== "settings");

    // Map each key to a labeled input
    return topLevel.map((key) => {

      // Compute a readable label
      const label = key.charAt(0).toUpperCase() + key.slice(1);

      // Decide input type: plain text except when key is "dpi" (numeric)
      const isNumeric = key.toLowerCase() === "dpi";

      // Current value for this field
      const value = formValues?.[key] ?? "";

      // Return early if this is 'methods' (included in materials)
      if (key === "methods" ) return null;

      // Render input with BEM classes
      return (
        
          // Wrapper div for a single field row 
          <div key={key} className="array-manager__field">

            <label className="array-manager__label">{label}</label>

            <input
              className="array-manager__input"
              type={isNumeric ? "number" : "text"}
              name={key}
              value={value ?? ""}
              onChange={(e) => {
                handleChange(e);         
              }}
              placeholder={`Enter ${label.toLowerCase()}`}
              disabled={isCoreData}
            />

          </div>

      );

    });

  };



  // -----------------------
  // Main render
  // -----------------------
  return (

    <div className="array-manager">

      {/* Sidebar with type dropdown, add new, and list */}
      <aside className="array-manager__sidebar">

        {/* Type selector */}
        <div className="array-manager__control-group">

          
          <select
            className="array-manager__select"
            value={selectedType}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedType(value);
              fetchList(value);
            }
            }
          >
            <option value="select">Select Type</option>
            <option value="Materials">Materials</option>
            <option value="Methods">Methods</option>
            <option value="Modes">Modes</option>

          </select>

        </div>
       
        {/* Error area */}
        {listError && <div className="array-manager__error">{listError}</div>}

        {/* Scrollable list */}
        <div className="array-manager__list">

          {/* Sort selector */}
          <div className="array-manager__control-group array-manager__control-group--row">
            
            {/* Sort selector */}
            <select
              className="array-manager__sort-select"
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              title="Sort list"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="az">A → Z</option>
              <option value="za">Z → A</option>
            </select>
          </div>

          {isLoadingList && <div className="array-manager__loading">Loading…</div>}

          {!isLoadingList && sortedList.length === 0 && <div className="array-manager__empty">No items found.</div>}

          {!isLoadingList &&

            sortedList.map((item) => {

              const isActive = !isAddingNew && selectedId === item.id;

              return (

                <button
                  key={item.id}
                  type="button"
                  className={`array-manager__list-item${isActive ? " array-manager__list-item--active" : ""}`}
                  onClick={() => handleSelectItem(item.id)}
                  title={item.description || item.name || `#${item.id}`}
                >

                  <span className="array-manager__list-item-name">{item.name || "(unnamed)"}</span>
                  <span className="array-manager__list-item-id">#{item.id}</span>

                </button>

              );

            })}

        </div>

            {/* Add new button */}
            { selectedType !== "select" &&

              <>

                <CreateButton type="button" tooltip={"Add New " + selectedType} title="Add New" size="m" onClick={handleAddNewMMM} />

              </>

            }

      </aside>

      
      {/* Right side details panel - only show if something is selected or we're adding a new item */}
      { (( !isAddingNew && selectedId ) || isAddingNew ) &&
      
      <section className="array-manager__detail">

        {/* Header shows context */}
        <div className="array-manager__detail-header">
          
          <h3 className="array-manager__detail-title">
            {isAddingNew ? `Add New ${selectedType.slice(0, -1)}` : selectedId ? `Edit ${selectedType.slice(0, -1)} - ${itemDetails?.name}` : "Nothing Selected"}
          </h3>

        </div>

        {/* Form body */}
        <div className="array-manager__form">

          {/* Top-level dynamic fields (e.g., name, description, dpi, etc.) */}
          {renderTopLevelFields()}

          <br />

          {/* Nested-level fields (settings) */}
          {RenderSettingsSection(itemDetails)}
                  
          <br />

          {/* Actions: Save / Cancel */}
          { !isCoreData && 
          <div className="array-manager__actions">

            <CreateButton 
              title="Save"
              tooltip={formIsPartlyEdited ? "Save changes" : "No changes to save"} 
              size="m" 
              onClick={() => { handleSave(selectedType) }}
              disabled={!formIsPartlyEdited}
            />

            <CreateButton 
              title="Cancel"
              isCancel={true}
              tooltip={formIsPartlyEdited ? "Discard unsaved changes" : "Nothing to cancel"}
              size="m" 
              onClick={handleCancel}
              disabled={!formIsPartlyEdited}
            />

          {/* Delete only when editing an existing item */}
          {!isAddingNew && selectedId && !isCoreData && (

            <CreateButton tooltip="Delete" size="m" icon="/images/icon_bin.png" onClick={() => handleDelete(selectedType, selectedId)} />

          )}

          </div>

        }

        </div>
        
      </section>

      }

    </div>

  );

}
